import { DefaultFormFieldLayout } from '@bambi/component-library/src/components/forms/DefaultFormFieldLayout/DefaultFormFieldLayout';
import { Combobox } from '@bambi/component-library/src/components/inputs/Combobox/Combobox';
import { StyledOption } from '@bambi/component-library/src/components/inputs/Combobox/StyledOption';
import { ClockIcon } from '@heroicons/react/24/outline';

import { pricingNameRoot } from './pricingNameRoot';
import { usePayers } from './usePayers';
import type {
  PassengerRead,
  PayerOrganizationRead,
  TripRead,
} from 'src/common/external/bambi-api/bambiApi';
import { FormFieldController } from 'src/common/FormField/FormFieldController';

export function PayerSelect({
  fieldName,
  selectedPassenger,
  selectedTrip,
  onPayerSelected,
}: {
  fieldName?: string;
  selectedPassenger?: PassengerRead | null;
  selectedTrip?: TripRead | null;
  onPayerSelected?: (payer: PayerOrganizationRead | null) => void;
}) {
  const {
    payerOptions,
    setPassengerSearchTerm,
    debounceSearch,
    payers,
    recentPayers,
    isLoading,
  } = usePayers({
    selectedPassengerId: selectedPassenger?.id,
    refetchOnMountOrArgChange: true,
  });

  const hasIntermediary = Boolean(selectedTrip?.intermediary);

  return (
    <div className="grow">
      <FormFieldController
        name={fieldName ?? `${pricingNameRoot}.payer`}
        rules={{ required: 'Please choose a payer' }}
      >
        {({ field, fieldState }) => {
          return (
            <DefaultFormFieldLayout
              label={`Payer * ${isLoading ? '(Loading...)' : ''}`}
              error={fieldState.error?.message?.toString()}
              inputId={`${pricingNameRoot}.payer`}
            >
              <Combobox
                id={`${pricingNameRoot}.payer`}
                value={
                  field.value
                    ? payerOptions.find(
                        (option) => option.value === field.value
                      )
                    : undefined
                }
                placeholder="Select a payer"
                options={payerOptions}
                onChange={(selected) => {
                  const selectedPayer =
                    payers?.results.find((x) => x.id === selected.value) ||
                    recentPayers?.results.find(
                      (x) => x.payer_organization.id === selected.value
                    )?.payer_organization;
                  onPayerSelected?.(selectedPayer || null);
                  field.onChange(selected.value);
                }}
                dataTestId="payer-combobox"
                onQueryChange={debounceSearch}
                onButtonClick={() => setPassengerSearchTerm('')}
                renderOption={(option, selected, active) => {
                  const recentAddress = recentPayers?.results.find(
                    (payer) => payer.payer_organization.id === option.value
                  );

                  return (
                    <StyledOption
                      key={option.value}
                      option={option}
                      selected={selected}
                      active={active}
                      startAddornment={
                        !!recentAddress && (
                          <span>
                            <ClockIcon className="h-4 w-4" />{' '}
                          </span>
                        )
                      }
                    />
                  );
                }}
                error={fieldState.error?.message?.toString()}
                disabled={isLoading || hasIntermediary}
              />
            </DefaultFormFieldLayout>
          );
        }}
      </FormFieldController>
    </div>
  );
}
